import * as Sentry from '@sentry/react';

type SentryConfig = Record<string, unknown> &
  Sentry.BrowserOptions & {
    /** Shows the floating "Fehler melden" button. Pages for external users turn it off. */
    hasFeedbackWidget?: boolean;
  };

const feedbackIntegration = () =>
  Sentry.feedbackIntegration({
    colorScheme: 'system',
    showBranding: false,
    isNameRequired: false,
    isEmailRequired: false,
    // i18n
    triggerLabel: 'Fehler melden',
    triggerAriaLabel: 'Fehler melden',
    formTitle: 'Fehler melden',
    submitButtonLabel: 'Senden',
    cancelButtonLabel: 'Abbrechen',
    confirmButtonLabel: 'Bestätigen',
    addScreenshotButtonLabel: 'Screenshot hinzufügen',
    removeScreenshotButtonLabel: 'Screenshot löschen',
    nameLabel: 'Name',
    namePlaceholder: 'Dein Name',
    emailLabel: 'E-Mail-Adresse',
    emailPlaceholder: 'name@domain.com',
    isRequiredLabel: '(Pflichtfeld)',
    messageLabel: 'Beschreibung',
    messagePlaceholder:
      'Was ist der Fehler? Was hätte eigentlich passieren sollen?',
    successMessageText: 'Danke für deine Fehlermeldung!',
  });

export const initSentry = ({
  dsn,
  hasFeedbackWidget = true,
  router,
  ...rest
}: SentryConfig) => {
  if (!dsn) {
    // TODO: replace with invariant from `@vestigas/utils`
    throw new Error('dsn is required to work with Sentry');
  }

  Sentry.init({
    ...rest,
    beforeBreadcrumb(breadcrumb) {
      return breadcrumb.category === 'console' ||
        breadcrumb.category?.includes('ui.')
        ? null
        : breadcrumb;
    },
    dsn,

    // The customer portal does not initialise Sentry from here — it has its own
    // `Sentry.init` in `apps/customer/src/services/thirdParty.service.ts`.
    // Entries meant for the customer portal too have to be added in both places.
    ignoreErrors: [
      'No matching state found in storage',
      'Request failed with status code 401',
      // An expired session is a normal logout, not a fault. A refresh that
      // fails for any other reason throws a generic error and still reports.
      'SessionExpiredError',
      /Object Not Found Matching Id:\d+, MethodName:.+/,
    ],
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
      ...(hasFeedbackWidget ? [feedbackIntegration()] : []),
    ],
    profilesSampleRate: 1,
    replaysOnErrorSampleRate: 1,

    replaysSessionSampleRate: 0.1,

    tracesSampleRate: 1,
  });
};
